import type { Attachment } from "@/lib/types";
import { isVideoFile } from "@/lib/media/video";

/** CMS upload drawer / form labels — filled by the client in the translation DB. */
export type CmsMediaKind = "image" | "video" | "embed";

const KIND_SUFFIX: Record<CmsMediaKind, string> = {
  image: "Image",
  video: "Video",
  embed: "Embed",
};

export type CmsMediaLabelKey =
  | "submit"
  | "uploadSuccess"
  | "updateSuccess"
  | "uploadError"
  | "button"
  | "title";

export function cmsMediaTranslationKey(
  label: CmsMediaLabelKey,
  kind: CmsMediaKind,
): string {
  const suffix = KIND_SUFFIX[kind];
  const keys: Record<CmsMediaLabelKey, string> = {
    submit: `cms.add.media.submit${suffix}`,
    uploadSuccess: `cms.add.media.uploadSuccess${suffix}`,
    updateSuccess: `cms.add.media.updateSuccess${suffix}`,
    uploadError: `cms.add.media.uploadError${suffix}`,
    button: `cms.add.media.button${suffix}`,
    title: `cms.add.media.title${suffix}`,
  };
  return keys[label];
}

export function attachmentKindToCmsMediaKind(
  kind?: Attachment["kind"] | null,
): CmsMediaKind {
  if (kind === "video") return "video";
  if (kind === "embed") return "embed";
  return "image";
}

/** Kind reflected in the upload form (selected file, embed URL, or existing attachment). */
export function resolveCmsMediaKindForUpload(args: {
  file?: File | null;
  embedUrl?: string;
  isVideo?: boolean;
  isEmbed?: boolean;
  attachment?: Attachment | null;
}): CmsMediaKind {
  const trimmedEmbed = args.embedUrl?.trim();
  if (trimmedEmbed || args.isEmbed) return "embed";
  if (args.file) return isVideoFile(args.file) ? "video" : "image";
  if (args.isVideo) return "video";
  return attachmentKindToCmsMediaKind(args.attachment?.kind);
}
